import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { Calendar, Scale, ArrowRight, Play } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WhatsAppWidget from "../components/WhatsAppWidget";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CaseWatchPage() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    axios.get(`${API}/case-watch`)
      .then((res) => setCases(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Case Watch | Legal Insights & Court Judgments"
        description="Stay informed with Mheshe Attorneys' Case Watch — summaries of important court judgments in property, conveyancing and corporate governance law."
        url="https://mhesheattorneys.co.za/case-watch"
      />
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-charcoal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <p className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold mb-4">
              Legal Insights
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-white leading-tight tracking-tighter mb-6">
              Case Watch
            </h1>
            <p className="text-white/60 font-sans text-lg max-w-2xl mx-auto">
              Court judgments summarised — practical insights for property developers, investors and commercial operators.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Cases Grid */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-gold mx-auto" />
              <p className="mt-4 text-charcoal/60">Loading cases...</p>
            </div>
          ) : cases.length === 0 ? (
            <div className="text-center py-20">
              <Scale className="h-16 w-16 mx-auto text-charcoal/20 mb-4" />
              <p className="text-charcoal/60 font-sans text-lg mb-2">
                Case Watch is coming soon.
              </p>
              <p className="text-charcoal/40 font-sans text-sm">
                We're preparing expert summaries of important court judgments. Check back shortly.
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {cases.map((caseItem, index) => (
                <motion.article
                  key={caseItem.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group cursor-pointer border border-charcoal/10 hover:border-brand-gold/30 transition-all"
                  onClick={() => navigate(`/case-watch/${caseItem.id}`)}
                  data-testid={`case-card-${index}`}
                >
                  {/* Video/Image thumbnail */}
                  {caseItem.cover_image && (
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={caseItem.cover_image}
                        alt={caseItem.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      {caseItem.video_url && (
                        <div className="absolute inset-0 flex items-center justify-center bg-charcoal/30">
                          <div className="w-14 h-14 rounded-full bg-brand-gold flex items-center justify-center">
                            <Play className="h-6 w-6 text-charcoal ml-1" />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="p-6">
                    <div className="flex items-center gap-2 text-charcoal/50 mb-3">
                      <Calendar className="h-4 w-4" />
                      <span className="font-sans text-sm">{caseItem.judgment_date}</span>
                      <span className="text-brand-gold">|</span>
                      <span className="font-sans text-xs text-brand-gold uppercase">{caseItem.related_service}</span>
                    </div>

                    <h2 className="font-serif text-xl text-charcoal mb-3 group-hover:text-brand-gold transition-colors">
                      {caseItem.title}
                    </h2>

                    <p className="text-charcoal/60 font-sans text-sm leading-relaxed mb-4 line-clamp-2">
                      {caseItem.practical_question}
                    </p>

                    <div className="flex items-center text-brand-gold font-sans text-sm font-medium uppercase tracking-wider">
                      <span>Read Analysis</span>
                      <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-2 transition-transform" />
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-light-grey">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl text-charcoal mb-4">
            Need Legal Guidance?
          </h2>
          <p className="text-charcoal/60 font-sans mb-8 max-w-xl mx-auto">
            Our specialist team can advise on how recent judgments may affect your property development or business.
          </p>
          <Link to="/#lead-capture">
            <Button className="btn-gold px-8 py-4 text-base font-semibold uppercase tracking-wider rounded-none">
              Discuss Your Matter
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
      <WhatsAppWidget />
    </div>
  );
}
